import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import NotFound from '@/pages/NotFound';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const cat = categories.find((c) => c.id === article.category);

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.date,
      publisher: {
        '@type': 'Organization',
        name: '征途国际',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://ztroute.github.io/articles/${article.slug}`,
      },
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} | 征途国际官方下载站`}
        description={article.excerpt}
        keywords={`${article.title},征途国际,征途国际攻略,征途国际下载`}
        canonicalUrl={`https://ztroute.github.io/articles/${article.slug}`}
        ogType="article"
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-20 pb-14 bg-background-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '攻略资讯', path: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="bg-background-50 rounded-xl">
            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-4">
              {cat && (
                <Link
                  to="/categories"
                  className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium"
                >
                  {cat.name}
                </Link>
              )}
              <span className="text-sm text-foreground-400 flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="text-sm text-foreground-400 flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="text-sm text-foreground-400 flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
            </div>

            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8 leading-tight">
              {article.title}
            </h1>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
              {article.content.split('\n').map((line, i) => {
                if (line.startsWith('## ')) {
                  return (
                    <h2 key={i} className="font-heading text-lg md:text-xl font-bold text-foreground-950 mt-8 mb-4">
                      {line.replace('## ', '')}
                    </h2>
                  );
                }
                if (line.startsWith('### ')) {
                  return (
                    <h3 key={i} className="font-heading text-base md:text-lg font-bold text-foreground-900 mt-6 mb-3">
                      {line.replace('### ', '')}
                    </h3>
                  );
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  const clean = line.replace(/\*\*/g, '');
                  return (
                    <p key={i} className="font-semibold text-foreground-900 mt-4 mb-2">
                      <strong>{clean}</strong>
                    </p>
                  );
                }
                if (line.trim() === '') {
                  return <div key={i} className="h-2"></div>;
                }
                if (/^\d+\./.test(line.trim())) {
                  return (
                    <p key={i} className="text-foreground-700 mb-1">
                      {line}
                    </p>
                  );
                }
                return (
                  <p key={i} className="text-foreground-700 mb-3">
                    {line}
                  </p>
                );
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                <Link
                  to="/categories"
                  className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full hover:bg-background-200 transition-colors"
                >
                  征途国际
                </Link>
                {cat && (
                  <Link
                    to="/categories"
                    className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full hover:bg-background-200 transition-colors"
                  >
                    {cat.name}
                  </Link>
                )}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/articles/${item.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        title={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{item.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}